import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource } from '../../../shared/model/keys'
import { MAX_CHECK_GOODS, type CatalogIntentState, type CatalogRidIntent } from './types'

const initialState: CatalogIntentState = { byResource: {} }
const EMPTY: CatalogRidIntent = { page: 0, checkDeviceId: null, checkGoodIds: [] }

/** Ключ rid — в нижнем регистре и с «|»: сброс области ресурса убирает ровно его запись. */
export const catalogKey = (resourceId: string) => `${resourceId.toLowerCase()}|catalog`

function intentOf(state: CatalogIntentState, resourceId: string): CatalogRidIntent {
  const key = catalogKey(resourceId)
  state.byResource[key] ??= { ...EMPTY, checkGoodIds: [] }
  return state.byResource[key]
}

/**
 * Намерение каталога по rid: страница и отметки для проверки профиля.
 * Срок — `screen`: снимок кабинета перечитывается, а выбор для проверки —
 * дело одного захода.
 */
const catalogSlice = createSlice({
  name: 'aqsi/catalog',
  initialState,
  reducers: {
    catalogPageChanged(state, action: PayloadAction<{ resourceId: string; page: number }>) {
      intentOf(state, action.payload.resourceId).page = Math.max(0, Math.trunc(action.payload.page))
    },
    checkGoodToggled(state, action: PayloadAction<{ resourceId: string; goodId: string }>) {
      const intent = intentOf(state, action.payload.resourceId)
      const ids = intent.checkGoodIds
      intent.checkGoodIds = ids.includes(action.payload.goodId)
        ? ids.filter((id) => id !== action.payload.goodId)
        : ids.length < MAX_CHECK_GOODS
          ? [...ids, action.payload.goodId]
          : ids
    },
    checkDeviceChanged(state, action: PayloadAction<{ resourceId: string; deviceId: number | null }>) {
      intentOf(state, action.payload.resourceId).checkDeviceId = action.payload.deviceId
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.byResource, action.payload.resourceId))
  },
})

export const { catalogPageChanged, checkGoodToggled, checkDeviceChanged } = catalogSlice.actions
export const catalogReducer = catalogSlice.reducer

export function catalogIntentOf(state: CatalogIntentState, resourceId: string): CatalogRidIntent {
  return state.byResource[catalogKey(resourceId)] ?? EMPTY
}
